import { useCallback, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, emailNote, useFetch } from "@/lib/api";
import { fmtDate, fmtDateTime, gbp, REQUEST_STATUSES, APPT_STATUSES } from "@/lib/format";
import { can, useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PageHeader, Loading, ErrorBox, Section, StatusBadge, DemoTag, Detail, NativeSelect, Field } from "@/components/Common";
import { SimpleTable } from "@/components/SimpleTable";
import { ActivityList, NotesPanel } from "@/components/Activity";
import { PhotoGrid } from "@/components/PhotoGrid";
import { SlotPicker } from "@/components/SlotPicker";
import { AppointmentDialog } from "@/components/AppointmentDialog";

export function Requests() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [f, setF] = useState({ q: "", status: params.get("status") || "", service: "", date_from: "", date_to: "", appt_status: "" });
  const qs = new URLSearchParams(Object.entries(f).filter(([, v]) => v)).toString();
  const { data, loading, error } = useFetch(`/admin/requests?${qs}`);
  const { data: services } = useFetch("/admin/services");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div>
      <PageHeader title="Job requests" subtitle="Search and filter every enquiry submitted through the portal." />
      <div className="mb-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-6">
        <Input className="lg:col-span-2" placeholder="Search name, reference, postcode, email" aria-label="Search requests" value={f.q} onChange={set("q")} data-testid="requests-search-input" />
        <NativeSelect aria-label="Status filter" value={f.status} onChange={set("status")} data-testid="requests-status-filter"><option value="">All statuses</option>{REQUEST_STATUSES.map((s) => <option key={s}>{s}</option>)}</NativeSelect>
        <NativeSelect aria-label="Service filter" value={f.service} onChange={set("service")} data-testid="requests-service-filter"><option value="">All services</option>{services?.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</NativeSelect>
        <NativeSelect aria-label="Appointment status filter" value={f.appt_status} onChange={set("appt_status")} data-testid="requests-appt-filter"><option value="">Any appointment</option><option value="None">No appointment</option>{APPT_STATUSES.map((s) => <option key={s}>{s}</option>)}</NativeSelect>
        <div className="flex gap-1"><Input type="date" aria-label="Submitted from" value={f.date_from} onChange={set("date_from")} data-testid="requests-date-from" /><Input type="date" aria-label="Submitted to" value={f.date_to} onChange={set("date_to")} data-testid="requests-date-to" /></div>
      </div>
      <ErrorBox error={error} />
      {loading && !data ? <Loading /> : (
        <SimpleTable testId="requests-table" rows={data || []} onRow={(r) => nav(`/admin/requests/${r.id}`)} rowTestId={(r) => `request-row-${r.reference}`}
          cols={[
            { label: "Reference", render: (r) => <span className="font-medium">{r.reference}<DemoTag show={r.is_demo} /></span> },
            { label: "Customer", render: (r) => r.customer_name },
            { label: "Service", render: (r) => r.services.map((s) => s.name).join(", ") },
            { label: "Postcode", render: (r) => r.property.postcode },
            { label: "Submitted", render: (r) => fmtDate(r.created_at) },
            { label: "Status", render: (r) => <StatusBadge status={r.status} /> },
            { label: "Appointment", render: (r) => (r.appointment ? <StatusBadge status={r.appointment.status} /> : "—") },
            { label: "Quotation", render: (r) => (r.quotation ? `${r.quotation.reference} (${r.quotation.status})` : "—") },
          ]} />
      )}
    </div>
  );
}

function AdminBookDialog({ req, open, onClose, onDone }) {
  const [type, setType] = useState("On-site visit");
  const [slot, setSlot] = useState({ date: null, start: null });
  const [err, setErr] = useState(null);
  const slotsUrl = useCallback((d) => `/admin/availability/slots?date=${d}`, []);
  const save = async () => {
    try { const r = await api.post(`/admin/requests/${req.id}/appointments`, { type, ...slot }); toast.success(`Appointment booked and confirmed. ${emailNote(r.data.email_status)}`); onDone(); onClose(); }
    catch (e) { setErr(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-card"><DialogTitle>Create appointment for {req.reference}</DialogTitle>
        <DialogDescription>Booked appointments are confirmed immediately.</DialogDescription>
        <NativeSelect aria-label="Appointment type" value={type} onChange={(e) => setType(e.target.value)} data-testid="admin-appt-type-select">{["Telephone consultation", "Video consultation", "On-site visit"].map((t) => <option key={t}>{t}</option>)}</NativeSelect>
        <SlotPicker admin config={{ working_days: [0, 1, 2, 3, 4, 5, 6], unavailable_dates: [] }} slotsUrl={slotsUrl} value={slot} onChange={setSlot} />
        <ErrorBox error={err} />
        <Button onClick={save} disabled={!slot.start} data-testid="admin-appt-save-btn">Book appointment</Button>
      </DialogContent>
    </Dialog>
  );
}

export function RequestDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const { data: r, loading, error, reload } = useFetch(`/admin/requests/${id}`);
  const [status, setStatus] = useState("");
  const [info, setInfo] = useState("");
  const [book, setBook] = useState(false);
  const [appt, setAppt] = useState(null);
  if (loading && !r) return <Loading />;
  if (error) return <ErrorBox error={error} />;
  const updateStatus = async () => {
    try { await api.patch(`/admin/requests/${id}/status`, { status }); toast.success(`Status updated to ${status}`); setStatus(""); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const askInfo = async () => {
    try { const res = await api.post(`/admin/requests/${id}/request-info`, { message: info }); toast.success(`Request sent to customer portal. ${emailNote(res.data.email_status)}`); setInfo(""); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const convert = async () => {
    if (!window.confirm("Convert this request into a job?")) return;
    try { const j = await api.post(`/admin/requests/${id}/convert`, {}); toast.success(`Job ${j.data.reference} created`); nav(`/admin/jobs/${j.data.id}`); } catch (e) { toast.error(errMsg(e)); }
  };
  const c = r.customer;
  return (
    <div className="space-y-5">
      <PageHeader title={<>{r.reference}<DemoTag show={r.is_demo} /></>} subtitle={`Submitted ${fmtDateTime(r.created_at)}`}
        actions={<>
          <StatusBadge status={r.status} testId="admin-request-status" />
          {can(user, "appointments") && <Button size="sm" variant="outline" onClick={() => setBook(true)} data-testid="req-create-appt-btn">Create appointment</Button>}
          {can(user, "quotations") && <Button size="sm" variant="outline" onClick={() => nav(`/admin/quotations/new?request=${r.id}`)} data-testid="req-create-quote-btn">Create quotation</Button>}
          {can(user, "jobs") && !r.jobs.length && <Button size="sm" onClick={convert} data-testid="req-convert-btn">Convert to job</Button>}
        </>} />
      <div className="grid gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <Section title="Customer">
            <dl className="grid gap-3 sm:grid-cols-3">
              <Detail label="Name"><Link className="text-primary underline" to={`/admin/customers/${c.id}`} data-testid="req-customer-link">{c.name}</Link></Detail>
              <Detail label="Email"><a className="underline" href={`mailto:${c.email}`}>{c.email}</a></Detail>
              <Detail label="Telephone"><a className="underline" href={`tel:${c.phone}`}>{c.phone}</a></Detail>
            </dl>
          </Section>
          <Section title="Property">
            <dl className="grid gap-3 sm:grid-cols-2">
              <Detail label="Address">{r.property.address}</Detail><Detail label="Postcode">{r.property.postcode}</Detail>
              <Detail label="Type"><span className="capitalize">{r.property.property_type}</span></Detail>
              <Detail label="Access">{r.property.access_instructions}</Detail><Detail label="Parking / site access">{r.property.parking_info}</Detail>
            </dl>
          </Section>
          <Section title="Project">
            <dl className="grid gap-3 sm:grid-cols-2">
              <Detail label="Services">{r.services.map((s) => s.name).join(", ")}</Detail><Detail label="Budget">{r.budget || "Not given"}</Detail>
              <Detail label="Preferred start">{r.timeframe || "Flexible"}</Detail><Detail label="Deadline">{r.deadline}</Detail>
              <Detail label="Area">{r.area}</Detail><Detail label="Other requirements">{r.requirements}</Detail>
            </dl>
            <div className="mt-3"><Detail label="Description"><span className="whitespace-pre-wrap">{r.description}</span></Detail></div>
            <h3 className="mb-2 mt-5 text-sm font-semibold">Photographs ({r.photos.length})</h3>
            <PhotoGrid photos={r.photos} />
          </Section>
          <Section title="Activity history"><ActivityList items={r.activity} testId="req-activity" /></Section>
        </div>
        <div className="space-y-5">
          <Section title="Manage">
            <Field label="Update status" id="rs">
              <div className="flex gap-2">
                <NativeSelect id="rs" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="req-status-select"><option value="">Choose…</option>{r.allowed_statuses.filter((s) => s !== r.status).map((s) => <option key={s}>{s}</option>)}</NativeSelect>
                <Button size="sm" onClick={updateStatus} disabled={!status} data-testid="req-status-save-btn">Save</Button>
              </div>
            </Field>
            <div className="mt-4"><Field label="Request more information from customer" id="ri">
              <Textarea id="ri" value={info} onChange={(e) => setInfo(e.target.value)} placeholder="Visible to the customer" data-testid="req-info-input" />
            </Field>
              <Button className="mt-2" size="sm" variant="outline" onClick={askInfo} disabled={info.trim().length < 5} data-testid="req-info-send-btn">Send request</Button></div>
          </Section>
          <Section title="Appointments">
            {!r.appointments.length ? <p className="text-sm text-muted-foreground">None.</p> : r.appointments.map((a) => (
              <button key={a.id} onClick={() => setAppt(a.id)} data-testid={`req-appt-${a.id}`} className="mb-2 flex w-full items-center justify-between rounded-md border border-border p-3 text-left text-sm hover:bg-muted">
                <span>{a.type}<br /><span className="text-xs text-muted-foreground">{fmtDate(a.date)} {a.start}</span></span><StatusBadge status={a.status} />
              </button>))}
          </Section>
          <Section title="Quotations">
            {!r.quotations.length ? <p className="text-sm text-muted-foreground">None.</p> : r.quotations.map((q) => (
              <Link key={q.id} to={`/admin/quotations/${q.id}`} className="mb-2 flex items-center justify-between rounded-md border border-border p-3 text-sm hover:bg-muted" data-testid={`req-quote-${q.reference}`}>
                <span>{q.reference}<br /><span className="text-xs text-muted-foreground">{gbp(q.total)}</span></span><StatusBadge status={q.status} />
              </Link>))}
          </Section>
          {r.jobs.map((j) => <Section key={j.id} title="Job"><Link to={`/admin/jobs/${j.id}`} className="flex items-center justify-between text-sm underline">{j.reference}<StatusBadge status={j.status} /></Link></Section>)}
          <NotesPanel notes={r.notes} postUrl={`/admin/requests/${id}/notes`} onAdded={reload} />
        </div>
      </div>
      <AdminBookDialog req={r} open={book} onClose={() => setBook(false)} onDone={reload} />
      <AppointmentDialog id={appt} onClose={() => setAppt(null)} onChanged={reload} />
    </div>
  );
}
